import React, { useState } from "react";
import { Button, Checkbox, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { AvailableFilter } from "../../index";
import styles from "./FilterItem.module.scss";
import classNames from "classnames";
import dayjs from "dayjs";
import ChevronIcon from "assets/icons/ChevronIcon";
import { DateTimeRange } from "views/components/DatePicker";
import { VLDropdown } from "../../../VLDropdown/VLDropdown";

// What a custom range is stored as, and how it reads in the pill.
const RANGE_VALUE_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const RANGE_DISPLAY_FORMAT = "YYYY-MM-DD";

const isCustomRange = (filter: AvailableFilter, values: string[]) =>
  filter.type === "dateRange" && values.length === 2;

interface FilterItemProps {
  filter: AvailableFilter;
  selectedValues: string[];
  onFilterChange: (filterKey: string, values: string[] | null) => void;
  /** See TableFiltersProps.dropdownClassName. */
  dropdownClassName?: string;
}

function FilterItem({
  filter,
  selectedValues,
  onFilterChange,
  dropdownClassName,
}: FilterItemProps) {
  const [searchText, setSearchText] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempValues, setTempValues] = useState<string[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleDropdownOpenChange = (open: boolean) => {
    // The calendar is portalled to the body, so a click in it reads as outside this
    // dropdown. Stay open while it is showing.
    if (!open && calendarOpen) return;
    setDropdownOpen(open);

    if (open) {
      setTempValues(selectedValues);
    }
  };

  const handleOptionChange = (value: string, checked: boolean) => {
    setTempValues((prev) => {
      if (filter.type === "single" || filter.type === "dateRange") {
        return checked ? [value] : [];
      } else {
        if (checked) {
          return [...prev, value];
        } else {
          return prev.filter((v) => v !== value);
        }
      }
    });
  };

  const handleRangeChange = (range: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    setTempValues(
      range ? range.map((date) => date.format(RANGE_VALUE_FORMAT)) : [],
    );
  };

  const handleOK = () => {
    onFilterChange(filter.key, tempValues.length > 0 ? tempValues : null);
    setDropdownOpen(false);
  };

  const handleReset = () => {
    setTempValues([]);
    setSearchText("");
  };

  const getFilteredOptions = () => {
    return filter.options.filter((opt) =>
      opt.text.toLowerCase().includes(searchText.toLowerCase()),
    );
  };

  const getFilterText = () => {
    if (selectedValues.length === 0) {
      return { title: filter.title, selection: "", additionalCount: 0 };
    }
    if (isCustomRange(filter, selectedValues)) {
      const [from, to] = selectedValues.map((value) =>
        dayjs(value).format(RANGE_DISPLAY_FORMAT),
      );
      return {
        title: filter.title,
        selection: from === to ? from : `${from} – ${to}`,
        additionalCount: 0,
      };
    }
    if (selectedValues.length === 1) {
      const option = filter.options.find(
        (opt) => opt.value === selectedValues[0],
      );
      return {
        title: filter.title,
        selection: option?.text || selectedValues[0],
        additionalCount: 0,
      };
    }
    const firstOption = filter.options.find(
      (opt) => opt.value === selectedValues[0],
    );
    return {
      title: filter.title,
      selection: firstOption?.text || selectedValues[0],
      additionalCount: selectedValues.length - 1,
    };
  };

  const renderFilterDropdown = () => {
    const filteredOptions = getFilteredOptions();

    return (
      <div
        className={classNames(
          "ant-table-filter-dropdown",
          styles.filterDropdown,
        )}
      >
        {filter.searchable && (
          <div className="ant-table-filter-dropdown-search">
            <Input
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              size="small"
            />
          </div>
        )}

        <div className="ant-table-filter-dropdown-content">
          <div className={styles.dropdownContent}>
            {filteredOptions.map((option) => {
              const isChecked = tempValues.includes(option.value);

              const handleClick = () => {
                handleOptionChange(option.value, !isChecked);
              };

              return (
                <div
                  key={option.value}
                  className={styles.optionItem}
                  onClick={handleClick}
                >
                  <Checkbox checked={isChecked} className={styles.checkbox}>
                    {option.text}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>

        {filter.type === "dateRange" && (
          <div className={styles.customRange}>
            <div className={styles.customRangeLabel}>Custom range</div>
            <DateTimeRange
              value={
                isCustomRange(filter, tempValues)
                  ? [dayjs(tempValues[0]), dayjs(tempValues[1])]
                  : null
              }
              onChange={handleRangeChange}
              // Rendered inside the dropdown it opened upwards over the presets.
              // From the body it positions against the input and opens below.
              getPopupContainer={() => document.body}
              onOpenChange={setCalendarOpen}
              popupClassName={dropdownClassName}
            />
          </div>
        )}

        <div className={styles.buttonsWrapper}>
          <Button type="link" size="small" onClick={handleReset}>
            Reset
          </Button>
          <Button type="primary" size="small" onClick={handleOK}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  const filterText = getFilterText();
  const isActive = selectedValues.length > 0;

  return (
    <VLDropdown
      dropdownRender={renderFilterDropdown}
      trigger={["click"]}
      placement="bottomLeft"
      open={dropdownOpen}
      onOpenChange={handleDropdownOpenChange}
      overlayClassName={dropdownClassName}
    >
      <div
        // The unhashed names are a styling hook for pages whose background matches
        // the pill's own, where an inactive pill would otherwise disappear.
        className={classNames(styles.filterTrigger, "vlTableFilterTrigger", {
          [styles.active]: isActive,
          vlTableFilterTriggerActive: isActive,
        })}
      >
        <div className={styles.titleContainer}>
          <div className={styles.titleTextContainer}>
            <div className={styles.filterTitle}>
              {filterText.title}
              {filterText.selection && ":"}
            </div>
            {filterText.selection && (
              <div className={styles.filterSelection}>
                {filterText.selection}
              </div>
            )}
            {filterText.additionalCount > 0 && (
              <div className={styles.additionalCount}>
                +{filterText.additionalCount}
              </div>
            )}
          </div>
          <div className={styles.filterIcon}>
            <ChevronIcon direction={dropdownOpen ? "up" : "down"} />
          </div>
        </div>
      </div>
    </VLDropdown>
  );
}

export default FilterItem;
